import type {
  BatchEditFormValues,
  UseBatchEditFormProps,
} from "./batch-edit-form.type";

import { Form } from "antd";
import isEqual from "lodash/isEqual";
import { useEffect, useState } from "react";

import { useSidePanel } from "@/modules/side-panel";

import { getFieldApiError } from "./batch-edit-form.helper";

export const useBatchEditForm = <FieldValue = unknown>({
  fieldName,
  initialValue,
  isEqualFn,
  onMutateReady,
  status,
  error,
}: UseBatchEditFormProps<FieldValue>) => {
  const [form] = Form.useForm<BatchEditFormValues<FieldValue>>();
  const formValue = Form.useWatch(fieldName, form);

  const [showError, setShowError] = useState(true);
  const { closeSidePanel } = useSidePanel();

  const hasChanges = !(
    isEqualFn?.({ initialValue, formValue }) ?? isEqual(formValue, initialValue)
  );
  const hasError = showError ? status === "error" : false;
  const fieldApiError = showError
    ? getFieldApiError(fieldName, error ?? undefined)
    : null;

  useEffect(() => {
    if (status === "success") closeSidePanel();
  }, [status, closeSidePanel]);

  const onFormChange = () => {
    setShowError(false);
  };

  const onFormSubmit = (values: BatchEditFormValues<FieldValue>) => {
    if (!hasChanges) return;

    setShowError(true);
    onMutateReady(values[fieldName]);
  };

  return {
    form,
    formValue,
    onFormChange,
    onFormSubmit,
    hasChanges,
    hasError,
    fieldApiError,
  };
};
